/* eslint-disable indent */
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { useMutation } from 'react-query';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useGetAdjustmentsById, useGetProjectById } from 'utils/hooks/projects';
import { formatDatetime, mapError, toCamelCaseString } from 'utils/func';
import Loading from 'components/Loading';
import { ADJUSTMENT_STATUSES, ADJUSTMENT_TYPES, TOAST } from 'constant';
import ApproveModal from 'features/SubmitProjectProposal/components/ApproveModal';
import { reviewExtendImplementationTime } from 'apis/extendImplemetationTime.api';
import { toast } from 'layout';
import { reviewFinancialAdjustment } from 'apis/financialAdjustment.api';
import { reviewPersonnelAdjustment } from 'apis/personnelAdjustment.api';
import RejectModal from 'features/SubmitProjectProposal/components/RejectModal';
import { adjustmentRejectSchema } from 'features/SubmitProjectProposal/validation';
import ViewAdjustmentHistory from 'features/SubmitProjectProposal/components/ViewAdjustmentHistory';
import { useGetAllAdjustmentComments } from 'utils/hooks/adjustmentComments';

export default function AdjustmentViewList() {
  const { t } = useTranslation();
  const { projectId } = useParams();

  const [adjustment, setAdjustment] = useState(null);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [approvePersonnelAdjustmentVisible, setApprovePersonnelAdjustmentVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [isViewAdjustmentHistory, setIsViewAdjustmentHistory] = useState(false);
  const [adjustmentCommentsParams, setAdjustmentCommentsParams] = useState({
    adjustmentName: '',
    adjustmentId: '',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(adjustmentRejectSchema),
  });

  const { data: projectData, isLoading: isLoadingProjectData } = useGetProjectById(projectId, {});
  const projectProposal = useMemo(() => projectData?.projectProposal ?? {}, [projectData]);

  const {
    data: adjustmentData,
    isLoading: isLoadingAdjustmentData,
    refetch,
  } = useGetAdjustmentsById(projectId, {});

  const { data: adjustmentComments, isLoading: isLoadingAdjustmentComments } =
    useGetAllAdjustmentComments(adjustmentCommentsParams, {
      enabled: isViewAdjustmentHistory,
    });

  const { mutateAsync: reviewExtendImplementationTimeAsync } = useMutation(
    reviewExtendImplementationTime
  );
  const { mutateAsync: reviewFinancialAdjustmentAsync } = useMutation(reviewFinancialAdjustment);
  const { mutateAsync: reviewPersonnelAdjustmentAsync } = useMutation(reviewPersonnelAdjustment);

  const mappingUrlAdjustment = {
    FinancialAdjustment: 'financial-adjustment',
    ProjectDeadlineExtension: 'project-deadline-extensions',
    PersonnelAdjustment: 'personnel-adjustment',
  };

  const formatType = (value) => t(`projectAdjustment.type.${value.type}`);

  const formatDate = (time) => {
    const datetime = formatDatetime(time, 'DD/MM/YYYY HH:mm:ss');
    return <span className="text-color-secondary">{datetime}</span>;
  };

  const formatStatus = (value) => {
    const statusColorMapping = {
      create: 'var(--green-500)',
      update: 'var(--orange-500)',
      submitted: 'var(--blue-500)',
      approved: 'var(--cyan-500)',
      rejected: 'var(--red-500)',
      new: 'var(--green-500)',
    };

    return (
      <Tag
        style={{ background: statusColorMapping[toCamelCaseString(value.status)] }}
        value={t(`projectAdjustment.status.${toCamelCaseString(value.status)}`)}
        rounded
      />
    );
  };

  const formatActions = (value) => {
    let postFix = '';
    if (value.type === 'PersonnelAdjustment') {
      postFix = `?projectProposalId=${projectProposal?.id}&projectId=${projectId}`;
    }

    return (
      <div className="flex flex-wrap justify-content-center gap-1">
        <Link
          to={`/projects-submit/${mappingUrlAdjustment[value.type]}/${projectId}/${
            value.id
          }${postFix}`}
        >
          <Button
            icon="pi pi-pencil"
            tooltip={t('projectAdjustment.actions.viewAndComment')}
            tooltipOptions={{ position: 'left', at: 'left top' }}
            severity="info"
            style={{ width: '42px', height: '42px' }}
          />
        </Link>

        {value?.status === ADJUSTMENT_STATUSES.SUBMITTED && (
          <>
            <Button
              icon="pi pi-check"
              tooltip={t('projectAdjustment.actions.approve')}
              tooltipOptions={{ position: 'left', at: 'left top' }}
              severity="success"
              style={{ width: '42px', height: '42px' }}
              onClick={() => {
                setAdjustment(value);
                if (value.type === ADJUSTMENT_TYPES.PERSONNEL_ADJUSTMENT) {
                  setApprovePersonnelAdjustmentVisible(true);
                } else {
                  setApproveModalVisible(true);
                }
              }}
            />
            <Button
              icon="pi pi-times"
              tooltip={t('projectAdjustment.actions.reject')}
              tooltipOptions={{ position: 'left', at: 'left top' }}
              severity="warning"
              style={{ width: '42px', height: '42px' }}
              onClick={() => {
                setAdjustment(value);
                setRejectModalVisible(true);
              }}
            />
          </>
        )}
        <Button
          icon="pi pi-history"
          tooltip={t('projectAdjustment.actions.viewAdjustmentHistory')}
          tooltipOptions={{ position: 'left', at: 'left top' }}
          severity="primary"
          style={{ width: '42px', height: '42px' }}
          onClick={() => {
            setAdjustmentCommentsParams({
              adjustmentName: value?.type,
              adjustmentId: value?.id,
            });
            setIsViewAdjustmentHistory(true);
          }}
        />
      </div>
    );
  };

  const handleApproveOrReject = async (data) => {
    const dataSender = {};
    if (rejectModalVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.REJECTED;
      dataSender.rejectionReason = data?.rejectionReason;
    } else if (approveModalVisible || approvePersonnelAdjustmentVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.APPROVED;
    }
    try {
      let aprroveOrRejectMutateion;
      if (adjustment?.type === ADJUSTMENT_TYPES.FINANCIAL_ADJUSTMENT) {
        aprroveOrRejectMutateion = reviewFinancialAdjustmentAsync;
      }
      if (adjustment?.type === ADJUSTMENT_TYPES.PROJECT_DEADLINE_EXTENSION) {
        aprroveOrRejectMutateion = reviewExtendImplementationTimeAsync;
      }
      if (adjustment?.type === ADJUSTMENT_TYPES.PERSONNEL_ADJUSTMENT) {
        aprroveOrRejectMutateion = reviewPersonnelAdjustmentAsync;
      }
      const res = await aprroveOrRejectMutateion({ id: adjustment?.id, body: dataSender });
      if (res?.status.toString().startsWith('2')) {
        toast(
          TOAST.SUCCESS,
          t(`projectAdjustment.${rejectModalVisible ? 'rejectModal' : 'approveModal'}.success`)
        );
        refetch();
        setApproveModalVisible(false);
        setRejectModalVisible(false);
        setApprovePersonnelAdjustmentVisible(false);
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const footerConfirmSubmit = () => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setApprovePersonnelAdjustmentVisible(false)}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        type="button"
        onClick={handleApproveOrReject}
      />
    </div>
  );

  return (
    <>
      {(isLoadingProjectData || isLoadingAdjustmentData) && <Loading />}
      <div className="p-5 text-lg border">
        <h3 className="mb-5 line-height-3 text-4xl text-center font-bold">
          {t('projectAdjustment.title')}
        </h3>
        <div className="mb-4">
          {/* project info */}
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.vietnameseName')}:
            </div>
            <div className="flex-1">{projectProposal?.vietnameseName}</div>
          </div>
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
            </div>
            <div className="flex-1">{projectProposal?.englishName}</div>
          </div>
        </div>

        {/* adjustments list */}
        <DataTable
          value={adjustmentData.data}
          className="p-datatable-gridlines header-table"
          showGridlines
          dataKey="id"
          filterDisplay="menu"
          scrollable
          //   loading={isLoading}
          emptyMessage={t('projectAdjustment.empty')}
        >
          <Column
            header="TT"
            frozen
            alignFrozen="left"
            body={(rowData, { rowIndex }) => rowIndex + 1}
            style={{ width: '3rem', textAlign: 'center' }}
          />
          <Column
            field="requestedBy"
            // body={formatVietnameseName}
            header={t('projectAdjustment.requestedBy')}
            style={{
              minWidth: '10rem',
              maxWidth: '16rem',
              textAlign: 'center',
            }}
            frozen
            alignFrozen="left"
          />

          <Column
            field="requestedAt"
            body={(rowData) => formatDate(rowData?.requestedAt)}
            header={t('projectAdjustment.requestedAt')}
            style={{
              minWidth: '4rem',
              maxWidth: '6rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="reviewedBy"
            header={t('projectAdjustment.reviewedBy')}
            style={{
              minWidth: '10rem',
              maxWidth: '16rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="reviewedAt"
            body={(rowData) => formatDate(rowData?.reviewedAt)}
            header={t('projectAdjustment.reviewedAt')}
            style={{
              minWidth: '4rem',
              maxWidth: '6rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="type"
            body={formatType}
            header={t('projectAdjustment.type.label')}
            style={{
              minWidth: '10rem',
              maxWidth: '16rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="rejectionReason"
            header={t('projectAdjustment.rejectionReason')}
            style={{
              minWidth: '12rem',
              maxWidth: '16rem',
              textAlign: 'left',
            }}
          />

          <Column
            header={t('projectAdjustment.status.label')}
            body={formatStatus}
            style={{ width: '10rem', textAlign: 'center' }}
          />

          <Column
            header={t('project.action')}
            body={formatActions}
            style={{ width: '10rem', textAlign: 'center' }}
            frozen
            alignFrozen="right"
          />
        </DataTable>
      </div>

      <ApproveModal
        visible={approveModalVisible}
        onClose={() => setApproveModalVisible(false)}
        onSubmit={handleApproveOrReject}
        isSubmitting={false}
      />

      <RejectModal
        visible={rejectModalVisible}
        onClose={() => setRejectModalVisible(false)}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={handleApproveOrReject}
        isDeadline={false}
      />

      <ViewAdjustmentHistory
        data={adjustmentComments}
        visible={isViewAdjustmentHistory}
        setVisible={setIsViewAdjustmentHistory}
        title={t('projectAdjustment.viewList.viewAdjustmentHistory')}
        isLoading={isLoadingAdjustmentComments}
      />

      <Dialog
        visible={approvePersonnelAdjustmentVisible}
        header={<p className="text-blue-500">{t('editHumanResource.dialogConfirm.title')}</p>}
        position="center"
        style={{ width: '410px' }}
        onHide={() => setApprovePersonnelAdjustmentVisible(false)}
        footer={() => footerConfirmSubmit()}
      >
        <div className="text-lg py-3">{t('editHumanResource.dialogConfirm.content')}</div>
        <span className="text-md py-3 ">{t('editHumanResource.dialogConfirm.attention')}</span>
        <span className="text-md py-3 font-bold">
          {t('editHumanResource.dialogConfirm.warning')}
        </span>
      </Dialog>
    </>
  );
}
